import { captureException } from '@sentry/react';
import ky from 'ky';

import { AuthService } from '@/entities/auth/auth-service';

// Promise to track ongoing refresh attempts
let refreshPromise: Promise<void> | undefined = undefined;

// Map to track retry attempts per request URL
const retryAttemptsMap = new Map<string, number>();
const MAX_RETRY_ATTEMPTS = 3;

// Check if error is a network error
const isNetworkError = (error: unknown) =>
  error instanceof TypeError && error.message.includes('Failed to fetch');

export const socialApiClient = ky.create({
  prefixUrl: `${import.meta.env.VITE_SOCIAL_API_BASE_URL}`,
  retry: {
    limit: 3,
  },
  hooks: {
    beforeRequest: [
      async (request) => {
        // Skip auth endpoints
        if (request.url.includes('/auth/')) {
          return;
        }

        // Refresh token if expired
        if (AuthService.isAccessTokenExpired() && !refreshPromise) {
          refreshPromise = AuthService.refreshToken()
            .catch(() => {
              // Ignore errors - afterResponse will handle 401
            })
            .finally(() => {
              refreshPromise = undefined;
            });
        }

        // Wait for any ongoing refresh
        if (refreshPromise) {
          try {
            await refreshPromise;
          } catch {
            // Continue with current token
          }
        }

        // Set authorization header
        const token = AuthService.getTokens()?.accessToken;
        if (token != null) {
          request.headers.set('Authorization', `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        // Clear retry counter on successful response
        if (response.ok) {
          retryAttemptsMap.delete(request.url);
        }

        // Only handle 401 for non-auth endpoints
        if (response.status === 401 && !request.url.includes('/auth')) {
          // Get current retry count for this URL
          const currentRetries = retryAttemptsMap.get(request.url) || 0;

          // Check if we've exceeded max retries
          if (currentRetries >= MAX_RETRY_ATTEMPTS) {
            // Clear the retry counter
            retryAttemptsMap.delete(request.url);

            // Log the max retry exceeded
            captureException(new Error('Max token refresh retries exceeded'), {
              extra: {
                url: request.url,
                retries: currentRetries,
              },
            });

            // Clear auth and redirect
            AuthService.removeTokens();
            window.location.href = '/login';
            return response;
          }

          // No refresh token? Clear auth and return
          if (!AuthService.getTokens()?.refreshToken) {
            AuthService.removeTokens();
            retryAttemptsMap.delete(request.url);
            return response;
          }

          // Increment retry counter
          retryAttemptsMap.set(request.url, currentRetries + 1);

          // Try to refresh token
          // Avoid duplicate refreshToken calls
          if (refreshPromise == null) {
            refreshPromise = AuthService.refreshToken().finally(() => {
              refreshPromise = undefined;
            });
          }

          try {
            await refreshPromise;
            // Retry with new token
            return ky(request, options);
          } catch (error) {
            const tokens = AuthService.getTokens();

            // Clear retry counter on refresh failure
            retryAttemptsMap.delete(request.url);

            // If it's a network error, don't logout the user
            if (isNetworkError(error)) {
              captureException(error, {
                extra: {
                  context: 'network error during token refresh',
                  url: request.url,
                  retryAttempt: currentRetries + 1,
                },
              });
              // Return the original 401 response without logging out
              return response;
            }

            // Only logout if it's truly an authentication error
            captureException(error, {
              extra: {
                context: 'token refresh failed',
                authorizationHeader: request.headers?.get?.('Authorization'),
                access_token: tokens?.accessToken,
                refresh_token: tokens?.refreshToken,
                retryAttempt: currentRetries + 1,
              },
            });
            AuthService.removeTokens();
            window.history.replaceState({}, '', '/login');
            throw error;
          }
        }

        return response;
      },
    ],
    beforeError: [
      async (error) => {
        const responseBody = await error.response
          ?.clone()
          .text()
          .catch(() => null);

        // Extract response headers
        const responseHeaders: Record<string, string> = {};
        if (error.response?.headers) {
          error.response.headers.forEach((value, key) => {
            responseHeaders[key] = value;
          });
        }

        captureException(error, {
          tags: {
            errorType: isNetworkError(error) ? 'network' : 'http',
            httpMethod: error.request?.method || 'unknown',
            responseStatus: error.response?.status?.toString() || 'no-response',
          },
          extra: {
            url: error.request?.url,
            method: error.request?.method,
            responseHeaders,
            responseBody: responseBody?.substring(0, 1000), // First 1000 chars
            isNetworkError: isNetworkError(error),
          },
        });

        return error;
      },
    ],
  },
  timeout: 120_000,
});
